import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { toast } from "sonner";
import PaymentSummary from "@/components/PaymentSummary";
import QrButton from "@/components/QrButton";
import TopupFeePreview from "@/components/TopupFeePreview";
import {
  Plus, Copy, ExternalLink, Clock, CheckCircle2, XCircle, Ban, Loader2, CreditCard, Lock,
} from "lucide-react";

const fmtIST = (iso) => {
  if (!iso) return "—";
  try { const d = new Date(iso); return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }); }
  catch { return "—"; }
};

const STATUS_PILL = {
  pending: { color: "border-amber-300 text-amber-700 bg-amber-50", icon: Clock },
  paid: { color: "border-emerald-300 text-emerald-700 bg-emerald-50", icon: CheckCircle2 },
  expired: { color: "border-stone-300 text-stone-600 bg-stone-50", icon: Clock },
  inactive: { color: "border-orange-300 text-orange-700 bg-orange-50", icon: Clock },
  revoked: { color: "border-rose-300 text-rose-700 bg-rose-50", icon: Ban },
  failed: { color: "border-red-300 text-red-700 bg-red-50", icon: XCircle },
};

const payUrl = (link) => {
  const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, "");
  return `${base}/pay/${link.id}`;
};

export default function PartnerPaymentLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    wallet_target: "customer",
    customer_mobile: "",
    amount: "",
    min_amount: "",
    max_amount: "",
    remarks: "",
    expires_in_hours: 24,
    self_amount: false,
    suggested_amounts: "",
    max_attempts: "",
    max_successful_payments: "1",
    gateway: "",
    pg: "",
  });

  // Package-level PG feasibility (loaded once + on create-dialog open)
  const [feasibility, setFeasibility] = useState(null);

  const loadFeasibility = async () => {
    try {
      const { data } = await api.get("/admin/payment-links/partner/available-pgs");
      setFeasibility(data);
      // Auto-pick the first allowed gateway + channel
      const firstGw = (data?.gateways || []).find((g) => g.allowed);
      if (firstGw) {
        const firstCh = firstGw.supports_channel
          ? (firstGw.channels || []).find((c) => c.allowed)?.id || ""
          : "";
        setForm((f) => ({
          ...f,
          gateway: f.gateway || firstGw.key,
          pg: f.pg || firstCh,
        }));
      }
    } catch (err) {
      // Non-fatal — pickers will show empty allowed list
      console.error("Feasibility load failed", err);
    }
  };

  const loadLinks = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payment-links/partner/my-links");
      setLinks(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadLinks(); loadFeasibility(); }, []);

  const selectedGwEntry = useMemo(
    () => (feasibility?.gateways || []).find((g) => g.key === form.gateway),
    [feasibility, form.gateway]
  );

  const submitCreate = async () => {
    const amt = parseFloat(form.amount) || 0;
    if (!form.self_amount && (!amt || amt <= 0)) { toast.error("Amount required"); return; }
    if (form.wallet_target === "customer") {
      const m = (form.customer_mobile || "").replace(/\D/g, "").slice(-10);
      if (m.length !== 10 || !"6789".includes(m[0])) {
        toast.error("Enter a valid 10-digit customer mobile");
        return;
      }
    }
    setCreating(true);
    try {
      const payload = {
        wallet_target: form.wallet_target,
        amount: amt,
        remarks: form.remarks || null,
        expires_in_hours: parseInt(form.expires_in_hours || "24", 10),
        self_amount: form.self_amount,
        max_successful_payments: parseInt(form.max_successful_payments || "1", 10),
      };
      if (form.wallet_target === "customer") {
        payload.customer_mobile = form.customer_mobile.replace(/\D/g, "").slice(-10);
      }
      if (form.min_amount) payload.min_amount = parseFloat(form.min_amount);
      if (form.max_amount) payload.max_amount = parseFloat(form.max_amount);
      if (form.max_attempts) payload.max_attempts = parseInt(form.max_attempts, 10);
      if (form.suggested_amounts) {
        payload.suggested_amounts = form.suggested_amounts.split(",").map((s) => parseFloat(s.trim())).filter((n) => n > 0);
      }
      if (form.gateway) payload.gateway = form.gateway;
      if (form.pg) payload.pg = form.pg;
      const { data } = await api.post("/admin/payment-links/partner/create", payload);
      const url = payUrl(data.link);
      if (url) { await copyToClipboard(url, { successMessage: "Link created & URL copied" }); }
      setCreateOpen(false);
      setForm({ wallet_target: "customer", customer_mobile: "", amount: "", min_amount: "", max_amount: "", remarks: "", expires_in_hours: 24, self_amount: false, suggested_amounts: "", max_attempts: "", max_successful_payments: "1", gateway: "", pg: "" });
      loadLinks();
      loadFeasibility();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create link");
    } finally { setCreating(false); }
  };

  const copyUrl = (url) => copyToClipboard(url, { successMessage: "URL copied" });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="partner-payment-links">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Payment Links</h1>
            <p className="text-sm text-muted-foreground mt-1">Create payment links for wallet top-ups. Payments require admin approval before crediting.</p>
          </div>
          <Button onClick={() => setCreateOpen(true)} data-testid="create-link-btn">
            <Plus className="h-4 w-4 mr-1" /> Create link
          </Button>
        </div>

        <PaymentSummary />

        {/* Links table */}
        <div className="surface-card overflow-hidden">
          <table className="w-full text-sm" data-testid="partner-links-table">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="p-3 text-left font-medium">Status</th>
                <th className="p-3 text-left font-medium">Target</th>
                <th className="p-3 text-left font-medium">Amount</th>
                <th className="p-3 text-left font-medium">Created</th>
                <th className="p-3 text-left font-medium">Expires</th>
                <th className="p-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Loading...</td></tr>}
              {!loading && links.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No payment links yet</td></tr>}
              {links.map((l) => {
                const sp = STATUS_PILL[l.status] || STATUS_PILL.pending;
                const Icon = sp.icon;
                return (
                  <tr key={l.id} className="border-t hover:bg-accent/20">
                    <td className="p-3"><Badge variant="outline" className={`text-[10px] ${sp.color}`}><Icon className="h-3 w-3 mr-1" />{l.status}</Badge></td>
                    <td className="p-3 text-xs">{l.wallet_target === "customer" ? `Customer: ${(l.customer_snapshot?.mobile || l.customer_snapshot?.name || l.customer_id || "—")}` : "Self (my wallet)"}</td>
                    <td className="p-3 font-mono">{l.self_amount ? "Self-amount" : `₹${Number(l.amount || 0).toLocaleString("en-IN")}`}</td>
                    <td className="p-3 text-xs text-muted-foreground">{fmtIST(l.created_at)}</td>
                    <td className="p-3 text-xs text-muted-foreground">{fmtIST(l.expires_at)}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => copyUrl(payUrl(l))} title="Copy URL"><Copy className="h-3.5 w-3.5" /></Button>
                        <QrButton url={payUrl(l)} filename={`payment-link-${l.id?.slice(0, 8)}.png`} title="Show QR code" testId={`qr-btn-${l.id}`} />
                        <Button variant="ghost" size="sm" onClick={() => window.open(payUrl(l), "_blank")} title="Open"><ExternalLink className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5" /> Create wallet top-up link</DialogTitle>
            <DialogDescription>Payment requires admin approval before wallet credit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Wallet target</Label>
              <Select value={form.wallet_target} onValueChange={(v) => setForm({ ...form, wallet_target: v, customer_mobile: "" })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="partner">My wallet (self top-up)</SelectItem>
                  <SelectItem value="customer">Customer wallet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.wallet_target === "customer" && (
              <div>
                <Label>Customer mobile</Label>
                <div className="flex gap-2 mt-1">
                  <span className="inline-flex items-center px-3 rounded-md border bg-muted/50 text-sm font-mono text-muted-foreground">+91</span>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={form.customer_mobile}
                    onChange={(e) => setForm({ ...form, customer_mobile: e.target.value.replace(/\D/g, "") })}
                    placeholder="10-digit mobile"
                    className="font-mono"
                    data-testid="customer-mobile-input"
                  />
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">OTP will be sent here to authorise the top-up. New mobiles are auto-registered as a customer scoped to you.</p>
              </div>
            )}
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label className="text-sm">Self-amount</Label>
                <p className="text-[11px] text-muted-foreground">Payer chooses amount</p>
              </div>
              <Switch checked={form.self_amount} onCheckedChange={(v) => setForm({ ...form, self_amount: v })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{form.self_amount ? "Default amount (₹)" : "Amount (₹) *"}</Label>
                <Input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="font-mono mt-1" placeholder={form.self_amount ? "0" : "500"} />
              </div>
              <div>
                <Label>Expires (hours)</Label>
                <Input type="number" min="1" max="720" value={form.expires_in_hours} onChange={(e) => setForm({ ...form, expires_in_hours: e.target.value })} className="font-mono mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Min amount <span className="text-[10px] text-muted-foreground">opt</span></Label>
                <Input type="number" min="0" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} className="font-mono mt-1" placeholder="—" />
              </div>
              <div>
                <Label>Max amount <span className="text-[10px] text-muted-foreground">opt</span></Label>
                <Input type="number" min="0" value={form.max_amount} onChange={(e) => setForm({ ...form, max_amount: e.target.value })} className="font-mono mt-1" placeholder="—" />
              </div>
            </div>
            {form.self_amount && (
              <div>
                <Label>Suggested amounts <span className="text-[10px] text-muted-foreground">comma-separated</span></Label>
                <Input value={form.suggested_amounts} onChange={(e) => setForm({ ...form, suggested_amounts: e.target.value })} className="font-mono mt-1" placeholder="500, 1000, 2000" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Max attempts <span className="text-[10px] text-muted-foreground">opt</span></Label>
                <Input type="number" min="1" value={form.max_attempts} onChange={(e) => setForm({ ...form, max_attempts: e.target.value })} className="font-mono mt-1" placeholder="Unlimited" />
              </div>
              <div>
                <Label>Max success</Label>
                <Input type="number" min="1" value={form.max_successful_payments} onChange={(e) => setForm({ ...form, max_successful_payments: e.target.value })} className="font-mono mt-1" />
              </div>
            </div>
            <div>
              <Label>Remarks <span className="text-[10px] text-muted-foreground">opt</span></Label>
              <Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="mt-1" placeholder="e.g., Monthly top-up for customer" />
            </div>

            {/* Payment gateway + channel — gated by partner's package */}
            {feasibility && (
              <div className="space-y-2 rounded-lg border bg-muted/20 p-3" data-testid="pg-picker">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Payment gateway</Label>
                  <span className="text-[10px] text-muted-foreground font-mono">
                    Package: {feasibility.package?.name || "—"}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {(feasibility.gateways || []).map((g) => {
                    const isSelected = form.gateway === g.key;
                    const disabled = !g.allowed;
                    return (
                      <button
                        key={g.key}
                        type="button"
                        disabled={disabled}
                        onClick={() => {
                          // Auto-pick first allowed channel for the new gateway
                          const firstCh = g.supports_channel
                            ? (g.channels || []).find((c) => c.allowed)?.id || ""
                            : "";
                          setForm({ ...form, gateway: g.key, pg: firstCh });
                        }}
                        title={disabled ? "Upgrade your package to enable this gateway" : ""}
                        className={`relative text-left rounded-md border p-2 text-xs transition-colors ${
                          isSelected
                            ? "border-primary bg-primary/5 ring-1 ring-primary"
                            : disabled
                            ? "border-border bg-muted/40 text-muted-foreground cursor-not-allowed opacity-60"
                            : "border-border hover:bg-accent/40"
                        }`}
                        data-testid={`pg-gateway-${g.key}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">{g.label}</span>
                          {disabled && <Lock className="h-3 w-3" />}
                        </div>
                        {disabled && (
                          <div className="text-[10px] mt-0.5 italic">{g.reason}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {selectedGwEntry?.supports_channel && (selectedGwEntry.channels?.length > 0) && (
                  <div className="pt-1">
                    <Label className="text-xs">PG channel</Label>
                    <div className="grid grid-cols-3 gap-1.5 mt-1">
                      {selectedGwEntry.channels.map((c) => {
                        const isSelected = form.pg === c.id;
                        const disabled = !c.allowed || !c.active;
                        return (
                          <button
                            key={c.id}
                            type="button"
                            disabled={disabled}
                            onClick={() => setForm({ ...form, pg: c.id })}
                            title={disabled ? (c.reason || (c.active ? "Channel not in your package" : "Channel inactive")) : ""}
                            className={`relative rounded-md border px-2 py-1 text-[11px] font-mono transition-colors ${
                              isSelected
                                ? "border-primary bg-primary/5 ring-1 ring-primary"
                                : disabled
                                ? "border-border bg-muted/40 text-muted-foreground cursor-not-allowed opacity-60"
                                : "border-border hover:bg-accent/40"
                            }`}
                            data-testid={`pg-channel-${c.id}`}
                          >
                            {c.id}
                            {disabled && <Lock className="h-2.5 w-2.5 inline-block ml-1 -mt-0.5" />}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Locked channels need a package upgrade. Active channels indicated by gateway availability.
                    </p>
                  </div>
                )}
                {/* Live MDR + Settlement preview — what customer pays at PG and what's credited */}
                <div className="pt-2">
                  <TopupFeePreview
                    amount={form.amount}
                    gateway={form.gateway}
                    channel={form.pg}
                    testidPrefix="link-fee"
                    compact
                  />
                </div>
              </div>
            )}
          </div>

          {/* URL preview chip */}
          <div className="rounded-xl border border-blue-200 bg-blue-50/60 px-4 py-3" data-testid="url-preview">
            <div className="flex items-start gap-2.5">
              <CreditCard className="h-4 w-4 mt-0.5 text-blue-700 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold tracking-[0.12em] uppercase text-blue-700 mb-1">
                  Payer will land on
                </p>
                <code className="text-xs font-mono text-blue-900 break-all">
                  {window.location.origin}/p/&lt;id&gt;
                </code>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={submitCreate} disabled={creating}>{creating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Create link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
